import { Stack } from '@koob/margaret';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  SegmentedControlField,
  SelectField,
} from '../../../../components/Fields';
import React, { useCallback, useEffect, useRef } from 'react';
import TextFieldButton from '../../../../components/Fields/TextFieldButton';
import VisuallyHidden from '../../../../components/VisuallyHidden';
import PeriodsField from '../../../../components/Fields/PeriodsField';
import { EXPERIENCE_PRICING_SCHEMA } from '../Utilities/ExperienceValidationSchemas';
import { initialLocale } from '../../../../utils';
import {
  ExperiencePeriodStateEnum,
  ExperiencePricingTypeEnum,
  ExperienceTypeEnum,
} from '@koob/enums';
import { KoobButton, KoobInputStepperField } from '@koob/ui';
import { KoobEnableableSection } from '@koob/ui';
import { CurrencyField } from 'components/Fields/CurrencyField';
import getSymbolFromCurrency from 'currency-symbol-map';

function useCurrencyPrefix() {
  const [{ value }] = useField('currency');
  if (!value) return undefined;

  return (
    <span className="pr-2 border-r border-gray-300">
      {getSymbolFromCurrency(value)}
    </span>
  );
}

function AddPaxButton({ onClick, children, disabled = false }) {
  return (
    <KoobButton
      variant="soft"
      size="sm"
      disabled={disabled}
      onClick={() => onClick()}
    >
      <i className="fa-light fa-plus" aria-hidden="true" />
      {children}
    </KoobButton>
  );
}

function AdultPaxSection({ periodIndex, isReadOnly = false }) {
  const { t } = useTranslation('experiences');
  const [{ value }, meta] = useField(`periods[${periodIndex}].adultPax`);
  const currencyPrefix = useCurrencyPrefix();
  // Per item errors come back as an array; only the array level one is ours
  const sectionError = typeof meta.error === 'string' ? meta.error : undefined;

  return (
    <FieldArray
      name={`periods[${periodIndex}].adultPax`}
      render={({ push, remove }) => (
        <div className="flex flex-col space-y-5">
          {value?.length > 0 && (
            <div>
              {(value ?? []).map((elem, adultPaxIndex) => (
                <div className="flex mb-3" key={adultPaxIndex}>
                  <Stack gutterSize={1} alignY="end">
                    <TextFieldButton
                      type="number"
                      required
                      min={0}
                      widthClass="w-[160px]"
                      label={t('form.price')}
                      name={`periods[${periodIndex}].adultPax[${adultPaxIndex}].price`}
                      disabled={isReadOnly}
                      iconLeft={currencyPrefix}
                    />

                    <KoobInputStepperField
                      required
                      min={1}
                      label={t('form.fromPax')}
                      errorKey={`periods[${periodIndex}].adultPax[${adultPaxIndex}].paxRange`}
                      name={`periods[${periodIndex}].adultPax[${adultPaxIndex}].paxRange[0]`}
                      disabled={isReadOnly}
                    />

                    <KoobInputStepperField
                      required
                      min={1}
                      label={t('form.toPax')}
                      errorKey={`periods[${periodIndex}].adultPax[${adultPaxIndex}].paxRange`}
                      name={`periods[${periodIndex}].adultPax[${adultPaxIndex}].paxRange[1]`}
                      disabled={isReadOnly}
                    />

                    {!isReadOnly && (
                      <KoobButton
                        type="button"
                        variant="danger"
                        onClick={() => remove(adultPaxIndex)}
                      >
                        <VisuallyHidden>Delete</VisuallyHidden>
                        <i
                          className="fa-light fa-trash"
                          style={{ fontSize: 20 }}
                        />
                      </KoobButton>
                    )}
                  </Stack>
                </div>
              ))}
            </div>
          )}

          {!isReadOnly && (
            <AddPaxButton onClick={() => push({})}>
              {t('form.addAdultPax')}
            </AddPaxButton>
          )}

          {sectionError && meta.touched && (
            <p className="m-0 text-sm text-red-500">{sectionError}</p>
          )}
        </div>
      )}
    />
  );
}

function ChildrenPaxSection({ periodIndex, isReadOnly = false }) {
  const { t } = useTranslation('experiences');
  const [{ value }] = useField(`periods[${periodIndex}].childrenPax`);
  const currencyPrefix = useCurrencyPrefix();

  return (
    <FieldArray
      name={`periods[${periodIndex}].childrenPax`}
      render={({ push, remove }) => (
        <div className="flex flex-col space-y-5">
          {value?.length > 0 && (
            <div>
              {(value ?? []).map((elem, childrenPaxIndex) => (
                <div className="flex mb-3" key={childrenPaxIndex}>
                  <Stack gutterSize={1} alignY="end">
                    <TextFieldButton
                      type="number"
                      required
                      min={0}
                      widthClass="w-[160px]"
                      label={t('form.price')}
                      name={`periods[${periodIndex}].childrenPax[${childrenPaxIndex}].price`}
                      disabled={isReadOnly}
                      iconLeft={currencyPrefix}
                    />

                    <KoobInputStepperField
                      required
                      min={0}
                      label={t('form.fromAge')}
                      errorKey={`periods[${periodIndex}].childrenPax[${childrenPaxIndex}].ageRange`}
                      name={`periods[${periodIndex}].childrenPax[${childrenPaxIndex}].ageRange[0]`}
                      disabled={isReadOnly}
                    />

                    <KoobInputStepperField
                      required
                      min={0}
                      label={t('form.toAge')}
                      errorKey={`periods[${periodIndex}].childrenPax[${childrenPaxIndex}].ageRange`}
                      name={`periods[${periodIndex}].childrenPax[${childrenPaxIndex}].ageRange[1]`}
                      disabled={isReadOnly}
                    />

                    {!isReadOnly && (
                      <KoobButton
                        type="button"
                        variant="danger"
                        onClick={() => remove(childrenPaxIndex)}
                      >
                        <VisuallyHidden>Delete</VisuallyHidden>
                        <i
                          className="fa-light fa-trash"
                          style={{ fontSize: 20 }}
                        />
                      </KoobButton>
                    )}
                  </Stack>
                </div>
              ))}
            </div>
          )}

          {!isReadOnly && (
            <AddPaxButton onClick={() => push({})}>
              {t('form.addChildrenPax')}
            </AddPaxButton>
          )}
        </div>
      )}
    />
  );
}

function isPerPax(value) {
  return value === true || value === 'true';
}

function ExperiencePeriodPricing({ index, organization, isReadOnly = false }) {
  const { t } = useTranslation('experiences');
  const [field, , { setValue }] = useField(`periods[${index}]`);
  const [, , { setValue: setPrice }] = useField(`periods[${index}].price`);
  const [experienceType] = useField('type');
  const [pricingType] = useField('pricingType');
  const currencyPrefix = useCurrencyPrefix();

  // Get organization defaults
  const orgAutoConfirmEnabled =
    organization?.experienceAutoConfirmEnabled ?? false;
  const orgAutoConfirmDays = organization?.experienceAutoConfirmDays ?? 0;
  const defaultLabel = orgAutoConfirmEnabled
    ? t('form.autoConfirm.defaultYes')
    : t('form.autoConfirm.defaultNo');

  const isFit = pricingType.value === ExperiencePricingTypeEnum.FIT;
  const autoConfirmEnabled =
    field.value?.autoConfirmEnabled ?? orgAutoConfirmEnabled;
  const autoConfirmDays = field.value?.autoConfirmDays;

  if (
    !field.value.perPax &&
    (field.value.adultPax || field.value.childrenPax)
  ) {
    setValue({
      ...field.value,
      adultPax: undefined,
      childrenPax: undefined,
    });
  }

  // Reset the price when leaving per pax mode, it was frozen while per pax was on
  const perPax = field.value?.perPax;
  const previousPerPax = useRef(perPax);
  useEffect(() => {
    const wasPerPax = isPerPax(previousPerPax.current);
    const isNowPerPax = isPerPax(perPax);
    previousPerPax.current = perPax;

    // Legacy periods can carry a flat price next to per pax grids; the schema
    // rejects it on a field that is not rendered, which locks the wizard tabs
    const hasStalePrice =
      isNowPerPax && field.value?.price != null && field.value?.price !== '';
    if (wasPerPax !== isNowPerPax || hasStalePrice) {
      setPrice('');
    }
  }, [perPax]);

  useEffect(() => {
    if (
      pricingType.value === ExperiencePricingTypeEnum.SIC ||
      pricingType.value === ExperiencePricingTypeEnum.SERIES
    ) {
      setValue({
        ...field.value,
        perPax: true,
      });
    }
  }, [pricingType.value]);

  return (
    <div className="w-full">
      {isFit && (
        <div className="w-full flex flex-col">
          <div className="my-3 flex justify-between space-x-3">
            <SegmentedControlField
              label={t('form.periodType')}
              name={`periods[${index}].state`}
              required
              disabled={isReadOnly}
              options={[
                {
                  value: 'on_request',
                  label: t('form.onRequest'),
                },
                {
                  value: 'free_sale',
                  label: t('form.freeSale'),
                },
                {
                  value: 'stop_sale',
                  label: t('form.stopSale'),
                },
              ]}
            />

            <SegmentedControlField
              name={`periods[${index}].perPax`}
              options={[
                { label: t('form.no'), value: false },
                { label: t('form.yes'), value: true },
              ]}
              label={t('form.perPax')}
              required
              disabled={isReadOnly}
            />
          </div>

          {pricingType?.value === ExperiencePricingTypeEnum.FIT &&
            field.value?.state === ExperiencePeriodStateEnum.ON_REQUEST && (
              <KoobEnableableSection
                title={t('form.autoConfirm.title')}
                description={t(
                  !autoConfirmEnabled
                    ? 'form.autoConfirm.disabledDescription'
                    : 'form.autoConfirm.enabledDescription',
                  { days: autoConfirmDays ?? orgAutoConfirmDays },
                )}
                control={
                  <SegmentedControlField
                    name={`periods[${index}].autoConfirmEnabled`}
                    options={[
                      { label: defaultLabel, value: null },
                      { label: t('form.yes'), value: true },
                      { label: t('form.no'), value: false },
                    ]}
                    disabled={isReadOnly}
                  />
                }
                open={autoConfirmEnabled}
              >
                <TextFieldButton
                  name={`periods[${index}].autoConfirmDays`}
                  label={t('form.autoConfirm.days')}
                  type="number"
                  step="1"
                  min="0"
                  placeholder={String(orgAutoConfirmDays)}
                  required={field.value?.autoConfirmEnabled === true}
                  disabled={isReadOnly}
                />

                {field.value?.autoConfirmEnabled === null && (
                  <div className="mt-1 text-gray-600 italic">
                    {t('form.autoConfirm.leaveBlankToUseDefault')}
                  </div>
                )}
              </KoobEnableableSection>
            )}
        </div>
      )}

      {experienceType.value === 'Program' && false && (
        <div className="mt-5 flex space-x-3 items-end">
          <SegmentedControlField
            name={`periods[${index}].hasRoomSupplement`}
            options={[
              { label: t('form.no'), value: false },
              { label: t('form.yes'), value: true },
            ]}
            label={t('form.hasRoomSupplement')}
            required
          />

          {field.value.hasRoomSupplement && (
            <TextFieldButton
              type="number"
              required
              min="0"
              label={t('form.roomSupplement')}
              name={`periods[${index}].roomSupplement`}
            />
          )}
        </div>
      )}

      <div className="mt-3 flex justify-between">
        <div className="flex space-x-3">
          {!field.value?.perPax && (
            <TextFieldButton
              type="number"
              required
              min={0}
              widthClass="w-[160px]"
              label={t('form.price')}
              name={`periods[${index}].price`}
              disabled={isReadOnly}
              iconLeft={currencyPrefix}
            />
          )}
        </div>

        <div className="flex space-x-5">
          <div className="shrink-0">
            <KoobInputStepperField
              label={t('form.minimumPeopleRequired')}
              name={`periods[${index}].minPax`}
              min={1}
              required
              disabled={isReadOnly}
              touchRequired={false}
            />
          </div>
          <div className="shrink-0">
            <KoobInputStepperField
              label={t('form.maximumPeopleRequired')}
              name={`periods[${index}].maxPax`}
              min={1}
              required
              disabled={isReadOnly}
              touchRequired={false}
            />
          </div>
        </div>
      </div>

      {!isFit && (
        <div className="mt-3 flex justify-between">
          <div className="flex space-x-3">
            <TextFieldButton
              type="number"
              required
              step="1"
              min="0"
              label={t('form.allotments')}
              name={`periods[${index}].allotments`}
              defaultValue={0}
              disabled={isReadOnly}
            />

            <TextFieldButton
              type="number"
              step="1"
              min="0"
              label={t('form.allotmentsUsed')}
              name={`periods[${index}].allotmentsUsed`}
              defaultValue={0}
              disabled={isReadOnly}
            />
          </div>

          <div className="flex space-x-3">
            <KoobInputStepperField
              required
              min={1}
              label={t('form.minPaxForDeparture')}
              name={`periods[${index}].minPaxForDeparture`}
              disabled={field.value?.currentPaxForDeparture || isReadOnly}
            />

            <TextFieldButton
              type="number"
              step="1"
              min="0"
              label={t('form.currentPaxForDeparture')}
              name={`periods[${index}].currentPaxForDeparture`}
              disabled
            />
          </div>

          <div className="col-start-4">
            <TextFieldButton
              type="number"
              step="1"
              min="0"
              label={t('form.breakEven')}
              name={`periods[${index}].breakEven`}
              defaultValue={0}
              disabled={isReadOnly}
            />
          </div>
        </div>
      )}

      {field.value.perPax && (
        <div className="my-5 border-y flex flex-col divide-y">
          <div className="py-5">
            <AdultPaxSection periodIndex={index} isReadOnly={isReadOnly} />
          </div>

          <div className="py-5">
            <ChildrenPaxSection periodIndex={index} isReadOnly={isReadOnly} />
          </div>
        </div>
      )}
    </div>
  );
}

export function ExperiencePricing({
  currentLocale = null,
  organization,
  isReadOnly = false,
}) {
  const { t } = useTranslation('experiences');
  const [{ value: periods }, , { setValue: setPeriods }] = useField('periods');
  const [experienceType] = useField('type');
  const [isExtra] = useField('activity.isExtra');

  const customPeriodFields = useCallback(
    (props) => (
      <ExperiencePeriodPricing
        {...props}
        organization={organization}
        isReadOnly={isReadOnly}
      />
    ),
    [organization, isReadOnly],
  );
  const hasExistingPeriods = periods?.some((p) => p.id);
  const isProgram = experienceType.value === ExperienceTypeEnum.PROGRAM;
  // Departures carrying confirmed travellers are never removed from here, so
  // the delete button has nothing to do once they are the only ones left
  const bookedPeriodsCount = (periods ?? []).filter(
    (period) => period.currentPaxForDeparture,
  ).length;

  return (
    <>
      <div className="py-8 max-w-3xl mx-auto">
        <div className="w-full relative">
          {(currentLocale == null ||
            currentLocale.value !== initialLocale.value) && (
            <div className="absolute z-10 h-full w-full flex justify-center items-center">
              <div className="text-orange-500 px-3 py-2 bg-white border border-orange-500 rounded">
                {t('program.experiences.selectEnglish')}
              </div>
            </div>
          )}

          {experienceType.value === ExperienceTypeEnum.PROGRAM && (
            <div className="mb-5 p-5 border rounded-lg">
              <Stack gutterSize={4} className="flex items-end gap-4">
                <div className="flex-1">
                  <SelectField
                    label={t('form.pricingType')}
                    name="pricingType"
                    valueField={'value'}
                    options={[
                      {
                        label: t('form.fit'),
                        value: ExperiencePricingTypeEnum.FIT,
                      },
                      {
                        label: t('form.sic'),
                        value: ExperiencePricingTypeEnum.SIC,
                      },
                      {
                        label: t('form.series'),
                        value: ExperiencePricingTypeEnum.SERIES,
                      },
                    ]}
                    disabled={periods?.length > 0 || isReadOnly}
                  />
                </div>

                {periods?.length > 0 && isProgram && (
                  <CurrencyField
                    label={t('form.currency')}
                    disabled={hasExistingPeriods || isReadOnly}
                    tip={t('form.cannotEditCurrency')}
                    required
                  />
                )}
              </Stack>

              {periods?.length > 0 && (
                <div className="mt-2">
                  <div className="mb-1.5 text-k-orange font-medium">
                    {t('form.pricingTypeWarning')}
                  </div>

                  {bookedPeriodsCount > 0 && (
                    <div className="mb-1.5 text-gray-600">
                      {t('form.bookedPeriodsWarning', {
                        total: bookedPeriodsCount,
                      })}
                    </div>
                  )}

                  {!isReadOnly && (
                    <KoobButton
                      disabled={bookedPeriodsCount === periods.length}
                      onClick={async () => {
                        await setPeriods(
                          periods.filter(
                            (period) => period.currentPaxForDeparture,
                          ),
                        );
                      }}
                    >
                      {t('form.deletePeriods')}
                    </KoobButton>
                  )}
                </div>
              )}
            </div>
          )}

          {!isProgram && periods?.length > 0 && (
            // Currency is a single value for the whole experience; show it
            // once here instead of per period, where it used to be gated on
            // `perPax` and became unreachable once per pax pricing was on
            // (KD-3819)
            <div className="mb-5 max-w-xs">
              <CurrencyField
                label={t('form.currency')}
                disabled={hasExistingPeriods || isReadOnly}
                tip={t('form.cannotEditCurrency')}
                required
              />
            </div>
          )}

          <div
            className={
              (currentLocale == null ||
                currentLocale.value !== initialLocale.value) &&
              'pointer-events-none opacity-50'
            }
          >
            <PeriodsField
              required
              canRemoveLastElement
              name="periods"
              customPeriodFields={customPeriodFields}
              disabled={isReadOnly}
            />

            {(isExtra?.value === false || isExtra?.value === undefined) && (
              <div className="mt-5 flex-col space-y-5">
                <SegmentedControlField
                  label={t('form.isReservableAlone')}
                  name="isReservableAlone"
                  options={[
                    { label: t('form.yes'), value: true },
                    { label: t('form.no'), value: false },
                  ]}
                  required
                  disabled={isReadOnly}
                />

                {experienceType.value === ExperienceTypeEnum.PROGRAM && (
                  <>
                    <SegmentedControlField
                      label={t('form.isRepeatable')}
                      name="isRepeatable"
                      options={[
                        { label: t('form.yes'), value: true },
                        { label: t('form.no'), value: false },
                      ]}
                      required
                      disabled={isReadOnly}
                    />

                    <SegmentedControlField
                      label={t('form.isTransitionalProgram')}
                      name="isTransitionalProgram"
                      options={[
                        { label: t('form.yes'), value: true },
                        { label: t('form.no'), value: false },
                      ]}
                      required
                      disabled={isReadOnly}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const getSchema = () => {
  return EXPERIENCE_PRICING_SCHEMA;
};
